import { Component, computed, OnInit, OnDestroy, signal } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DropdownModule } from 'primeng/dropdown';
import { MessagesModule } from 'primeng/messages';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';

import { WaitDayOfMonthNode } from '@modules/campaigns/Domain/Nodes/WaitDayOfMonthNode';
import { WaitDayOfMonthConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { InspectorBase } from '@shared/components/joint-js/components/inspectors/inspector-base';
import { CampaignContextService } from '@shared/services/campaign-context.service';
import { CampaignModel } from '@modules/campaigns/models/campaign.model';
import { RegionalConfigInfoComponent } from '@shared/components/regional-config-info/regional-config-info.component';

@Component({
  selector: 'app-wait-day-of-month-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    DividerModule,
    SelectButtonModule,
    DropdownModule,
    MessagesModule,
    CardModule,
    TooltipModule,
    RegionalConfigInfoComponent,
  ],
  templateUrl: './wait-day-of-month.component.html',
  styleUrls: ['./wait-day-of-month.component.scss'],
})
export class WaitDayOfMonthInspectorComponent
  extends InspectorBase<WaitDayOfMonthNode>
  implements OnInit, OnDestroy
{
  dayOfMonth = signal<number | null>(1);
  partDay = signal<string | null>('MORNING');
  campaignSelected = signal<CampaignModel | null>(null);

  private campaignSub?: Subscription;

  partDayOptions = computed(() => [
    { label: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.MORNING'), value: 'MORNING' },
    { label: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.EVENING'), value: 'EVENING' },
    { label: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.FULL_DAY'), value: 'FULL_DAY' },
  ]);

  dayOfMonthOptions = computed(() => {
    // Del 1 al 30 son numéricos normales
    const options: { label: string; value: number }[] = Array.from({ length: 30 }, (_, i) => ({
      label: `${i + 1}`,
      value: i + 1,
    }));

    // Añadimos el "Último día" usando el valor numérico 31
    options.push({
      label: this.translate.instant(
        'DATA.MODULES.INSPECTORS.WAIT_DAY_OF_MONTH.CONFIG.LAST_DAY_OPTION',
      ),
      value: 31,
    });

    return options;
  });

  constructor(
    private workflow: WorkflowStateService,
    private translate: TranslateService,
    private campaignContext: CampaignContextService,
  ) {
    super();
  }

  ngOnInit() {
    this.campaignSub = this.campaignContext.campaign$.subscribe((campaign) => {
      if (campaign) {
        this.campaignSelected.set(campaign);
      }
    });
  }

  ngOnDestroy() {
    this.campaignSub?.unsubscribe();
  }

  override get nodeValue(): WaitDayOfMonthNode | null {
    const current = this.node();
    if (!current) return null;
    return current.type === NodeType.WaitDayOfMonth ? (current as WaitDayOfMonthNode) : null;
  }

  getTabs() {
    return [
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.WAIT_DAY_OF_MONTH.TABS.PREVIEW'),
        value: 'preview' as const,
      },
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.WAIT_DAY_OF_MONTH.TABS.CONFIG'),
        value: 'config' as const,
      },
    ];
  }

  onPartDayChange(value: string) {
    this.partDay.set(value);
    this.scheduleApplyChanges();
  }

  onDayOfMonthChange(value: number) {
    this.dayOfMonth.set(value);
    this.scheduleApplyChanges();
  }

  getSummaryMessage(): string {
    const partDay = this.getPartDayLabel().toLowerCase();
    const day = this.dayOfMonth();

    // Comparamos con 31 para saber si es el "Último día"
    if (day === 31) {
      return this.translate.instant(
        'DATA.MODULES.INSPECTORS.WAIT_DAY_OF_MONTH.PREVIEW.SUMMARY_MESSAGE_LAST',
        { partDay },
      );
    } else {
      return this.translate.instant(
        'DATA.MODULES.INSPECTORS.WAIT_DAY_OF_MONTH.PREVIEW.SUMMARY_MESSAGE_NUMERIC',
        { partDay, day },
      );
    }
  }

  private getPartDayLabel(): string {
    const partDay = this.partDay();
    if (partDay === 'MORNING') return this.translate.instant('DATA.COMMON.TIME_OF_WEEK.MORNING');
    if (partDay === 'EVENING') return this.translate.instant('DATA.COMMON.TIME_OF_WEEK.EVENING');
    if (partDay === 'FULL_DAY') return this.translate.instant('DATA.COMMON.TIME_OF_WEEK.FULL_DAY');
    return '-';
  }

  override applyChanges() {
    if (this.mode() === 'preview') return;

    const node = this.nodeValue;
    if (!node) return;

    const updatedConfig: WaitDayOfMonthConfig = {
      ...(node.config as WaitDayOfMonthConfig),
      partDay: this.partDay() || null,
      dayOfMonth: this.dayOfMonth() || null,
    };

    const updated = new WaitDayOfMonthNode(node.id, node.position, updatedConfig);
    updated.parentNodes = [...node.parentNodes];
    updated.childrenNodes = [...node.childrenNodes];

    const validation = updated.validate();
    this.errors.set(validation);

    if (!validation.length) {
      this.workflow.addOrUpdateNode(updated);
    }
  }

  override refreshFromConfig() {
    const node = this.nodeValue;
    if (!node) return;

    const cfg = (node.config ?? {}) as any;

    this.partDay.set(cfg.partDay ?? 'MORNING');
    this.dayOfMonth.set(cfg.dayOfMonth ?? 1);

    this.errors.set([]);
  }
}
